<template>
  <div class="ueditor-container">
    <!-- VueUeditorWrap 核心组件 -->
    <vue-ueditor-wrap v-model="editorContent" :config="ueditorConfig" :destroy="true" @ready="onEditorReady"
      @input="handleInput" />

    <!-- 自定义上传图片的隐藏上传组件 -->
    <el-upload ref="customUpload" class="hidden-upload" :action="uploadUrl" :on-success="handleCustomUploadSuccess"
      :before-upload="beforeCustomUpload" :file-list="uploadFileList" :auto-upload="true" multiple :limit="5"
      :on-exceed="handleUploadExceed" />
  </div>
</template>

<script>
import VueUeditorWrap from 'vue-ueditor-wrap'

export default {
  name: 'UEditor',
  components: { VueUeditorWrap },
  props: {
    value: { type: String, default: '<p>请输入内容...</p>' },
    height: { type: Number, default: 500 },
    showSourceBtn: { type: Boolean, default: true },
    allowPasteHtml: { type: Boolean, default: true },
    uploadUrl: { type: String, required: true },
    imgUrlPrefix: { type: String, required: true }
  },
  data() {
    return {
      editorContent: this.value,
      ueditorInstance: null,
      uploadFileList: [],
      ueditorConfig: {
        UEDITOR_HOME_URL: '/ueditor/',
        initialFrameWidth: '100%',
        initialFrameHeight: this.height,
        lang: 'zh-cn',
        langPath: '/ueditor/lang/',
        // 彻底移除原生 insertimage 按钮，只保留自定义按钮
        toolbars: [
          [
            'source', 'undo', 'redo',
            'bold', 'italic', 'underline', 'strikethrough',
            'fontsize', 'fontfamily', 'forecolor', 'backcolor',
            'justifyleft', 'justifycenter', 'justifyright',
            // 移除 insertimage，仅保留自定义按钮
            'inserttable', 'link', 'unlink',
            'removeformat', 'formatmatch', 'pasteplain',
            'searchreplace', 'fullscreen'
          ]
        ],
        // 彻底禁用所有上传相关配置
        serverUrl: '',
        disableAutoServerConfig: true,
        imageActionName: '',
        imageFieldName: '',
        imageAllowRemote: false,
        catchRemoteImageEnable: false,
        enableAutoSave: false,
        fileActionName: '',
        videoActionName: '',
        audioActionName: '',
        // 原有格式配置
        allowDivTransToP: false,
        removeFormatAttributes: '',
        safeHtmlRules: { '': ['all'] },
        inputPasteFilter: !this.allowPasteHtml,
        autoClearEmptyNode: false,
        retainOnlyLabelPasted: false,
        zIndex: 9999
      }
    }
  },
  watch: {
    value(newVal) {
      this.editorContent = newVal
    },
    editorContent(newVal) {
      this.$emit('input', newVal)
    },
    showSourceBtn(newVal) {
      const toolbars = this.ueditorConfig.toolbars[0]
      const sourceIndex = toolbars.indexOf('source')
      if (newVal && sourceIndex === -1) {
        toolbars.unshift('source')
      } else if (!newVal && sourceIndex !== -1) {
        toolbars.splice(sourceIndex, 1)
      }
    },
    allowPasteHtml(newVal) {
      this.ueditorConfig.inputPasteFilter = !newVal
    },
    uploadUrl(newVal) {
      // 修复：可选链不能用于赋值左侧，先判断再赋值
      if (this.$refs.customUpload) {
        this.$refs.customUpload.action = newVal
      }
    }
  },
  methods: {
    onEditorReady(editorInstance) {
      this.ueditorInstance = editorInstance
      console.log('UEditor 初始化完成，实例：', editorInstance)

      // 1. 手动添加自定义“插入图片”按钮（使用原生图标）
      this.addCustomImageButton()
      // 2. 彻底禁用所有原生上传入口（兜底）
      this.disableAllNativeUpload()

      this.$emit('ready', editorInstance)
    },

    // 核心：手动添加自定义“插入图片”按钮（复用UEditor原生图标）
    addCustomImageButton() {
      const _this = this

      setTimeout(function () {
        const toolbar = document.querySelector('.edui-toolbar')
        if (!toolbar) {
          console.warn('UEditor 工具栏未找到，无法添加自定义图片按钮')
          return
        }

        // 创建自定义按钮（复用原生图标类名，无需手动引用图片）
        const customBtn = document.createElement('div') // 改用div，匹配原生按钮结构
        // 完全复用UEditor原生图片按钮的类名，自动继承样式和图标
        customBtn.className = 'edui-default edui-btn-toolbar edui-for-insertimage'
        customBtn.title = '插入图片'
        customBtn.style.margin = '0 2px'
        customBtn.style.cursor = 'pointer'

        // 按钮内部结构完全匹配原生：icon容器 + 图标
        customBtn.innerHTML = `
          <div id="edui49" class="edui-box edui-button edui-for-insertimage edui-default">
            <div id="edui49_state"
              onmousedown="$EDITORUI[&quot;edui49&quot;].Stateful_onMouseDown(event, this);"
              onmouseup="$EDITORUI[&quot;edui49&quot;].Stateful_onMouseUp(event, this);"
              onmouseover="$EDITORUI[&quot;edui49&quot;].Stateful_onMouseOver(event, this);"
              onmouseout="$EDITORUI[&quot;edui49&quot;].Stateful_onMouseOut(event, this);"
              class="edui-default">
              <div class="edui-button-wrap edui-default">
                <div id="edui49_body" unselectable="on" title="图片上传"
                  class="edui-button-body edui-default"
                  onmousedown="return $EDITORUI[&quot;edui49&quot;]._onMouseDown(event, this);">
                  <div class="edui-box edui-icon edui-default"></div>
                  <div class="edui-box edui-label edui-default"></div>
                </div>
              </div>
            </div>
          </div>
        `

        // 按钮点击事件：触发自定义上传
        customBtn.onclick = function (e) {
          e.preventDefault()
          e.stopPropagation()
          _this.triggerCustomUpload()
        }

        // 插入到“右对齐”按钮后
        const targetBtn = toolbar.querySelector('.edui-btn[title="右对齐"]')
        if (targetBtn) {
          targetBtn.parentNode.insertBefore(customBtn, targetBtn.nextSibling)
        } else {
          toolbar.appendChild(customBtn)
        }
      }, 300)
    },

    // 彻底禁用所有原生上传入口
    disableAllNativeUpload() {
      const editor = this.ueditorInstance

      editor.ready(function () {
        // 禁用原生图片上传弹窗
        if (editor.getDialog) {
          const imageDialog = editor.getDialog('insertimage')
          if (imageDialog) {
            imageDialog.hide()
            imageDialog.show = function () { }
          }
        }

        // 禁用上传相关命令（确保即使有残留也无法触发）
        const blockCmds = ['insertimage', 'uploadimage', 'insertfile', 'uploadfile']
        blockCmds.forEach(function (cmd) {
          if (editor.commands[cmd]) {
            editor.commands[cmd].exec = function () {
              return false
            }
          }
        })

        // 禁用右键菜单上传选项
        if (editor.contextMenu && editor.contextMenu.items) {
          Object.keys(editor.contextMenu.items).forEach(function (key) {
            if (key.includes('image') || key.includes('file')) {
              delete editor.contextMenu.items[key]
            }
          })
        }
      })
    },

    // 触发自定义上传
    triggerCustomUpload() {
      const customUpload = this.$refs.customUpload
      if (customUpload) {
        const fileInput = customUpload.$el.querySelector('input[type="file"]')
        if (fileInput) {
          fileInput.click()
        } else {
          this.$message.error('上传组件未找到，请刷新页面重试')
        }
      } else {
        this.$message.error('上传组件初始化失败，请刷新页面重试')
      }
    },

    // 上传前校验
    beforeCustomUpload(file) {
      const isImg = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'].includes(file.type)
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isImg) {
        this.$message.error('仅支持JPG/PNG/GIF/BMP格式')
        return false
      }
      if (!isLt2M) {
        this.$message.error('图片大小不能超过2MB')
        return false
      }
      return isImg && isLt2M
    },

    // 上传成功：插入图片到编辑器
    handleCustomUploadSuccess(res, file) {
      if (!res || !res.data || !res.data.id) {
        this.$message.error('上传失败：返回格式错误')
        return
      }
      const imgUrl = this.imgUrlPrefix + res.data.id
      if (this.ueditorInstance && this.ueditorInstance.isReady) {
        const imgHtml = `<img src="${imgUrl}" style="max-width:100%;height:auto;margin:5px 0;" />`
        this.ueditorInstance.execCommand('insertHtml', imgHtml)
        this.ueditorInstance.execCommand('insertParagraph')
      }
      this.$message.success('图片上传成功！')
    },

    // 上传超出数量限制
    handleUploadExceed(files) {
      this.$message.warning(`最多上传5张图片，本次选择了${files.length}张`)
    },

    handleInput(html) {
      this.$emit('change', html)
    },
    setContent(html) {
      this.editorContent = html
    },
    getContent() {
      return this.editorContent
    }
  }
}
</script>

<style scoped>
.ueditor-container {
  width: 100%;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  overflow: hidden;
}

/* 修复工具栏样式 */
::v-deep .edui-toolbar {
  flex-wrap: wrap !important;
  padding: 4px !important;
  display: flex !important;
  align-items: center !important;
  line-height: 20px;
}

::v-deep .edui-container {
  border: none !important;
}

/* 隐藏原生上传组件 */
.hidden-upload {
  display: none !important;
}
</style>
